import * as React from "react"

import { FormFieldShell, type FormFieldProps } from "@/components/forms/FormField"
import { Input } from "@/components/ui/input"

export type FormInputProps = FormFieldProps &
  Omit<React.ComponentProps<typeof Input>, "className"> & {
    /** Class names applied to the input control (not the field wrapper). */
    inputClassName?: string
  }

/**
 * Labeled text input with description / error associations and required marker.
 *
 * Pass native input props (and react-hook-form `register` spreads) through.
 * Use `fieldClassName` for the Field wrapper and `inputClassName` for the input.
 *
 * @example RHF
 * ```tsx
 * <FormInput
 *   label="Email"
 *   type="email"
 *   required
 *   error={errors.email?.message}
 *   {...register("email")}
 * />
 * ```
 *
 * @example Controlled (no RHF)
 * ```tsx
 * <FormInput
 *   label="Password"
 *   type="password"
 *   value={password}
 *   onChange={(e) => setPassword(e.target.value)}
 *   required
 * />
 * ```
 */
export function FormInput({
  label,
  required,
  description,
  error,
  fieldClassName,
  inputClassName,
  id,
  ref,
  disabled,
  "aria-invalid": ariaInvalid,
  ...inputProps
}: FormInputProps) {
  const generatedId = React.useId()
  const inputId = id ?? generatedId
  const invalid =
    Boolean(error) ||
    (ariaInvalid != null && ariaInvalid !== false && ariaInvalid !== "false")

  return (
    <FormFieldShell
      label={label}
      required={required}
      description={description}
      error={error}
      fieldClassName={fieldClassName}
      htmlFor={inputId}
      invalid={invalid}
      disabled={disabled}
    >
      {(controlProps) => (
        <Input
          {...inputProps}
          {...controlProps}
          ref={ref}
          required={required}
          disabled={disabled}
          aria-invalid={ariaInvalid ?? (error ? true : undefined)}
          aria-describedby={
            [controlProps["aria-describedby"], inputProps["aria-describedby"]]
              .filter(Boolean)
              .join(" ") || undefined
          }
          className={inputClassName}
        />
      )}
    </FormFieldShell>
  )
}
